/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { INITIATE_UPLOAD_MUTATION } from '@/quote-management/api/uploadFile/graphql/InitiateUploadMutation';
import { FINISH_UPLOAD_MUTATION } from '@/quote-management/api/uploadFile/graphql/FinishUploadMutation';

type GraphQLError = { message: string };
type InitiateUploadData = {
  initiateUpload?: {
    upload_url: string;
    key: string;
    expires_at: string;
  };
};
type FinishUploadData = {
  finishUpload?: {
    success: boolean;
    key: string;
    message?: string;
  };
};
type GraphQLResponse<T> = {
  data?: T;
  errors?: GraphQLError[];
};

export const uploadFile = async (file: File): Promise<{ key: string }> => {
  const mediaResourceType = 'NEGOTIABLE_QUOTE_ATTACHMENT';

  try {
    const filename = file?.name;
    if (!file || !filename) throw new Error('Invalid file');

    const formatGraphQLErrors = (errors: GraphQLError[]): string =>
      errors.map((e) => e.message).join('; ');

    const { data: initialData, errors: initialErrors } = (await fetchGraphQl(
      INITIATE_UPLOAD_MUTATION,
      { 
        variables: { input: { key: filename, media_resource_type: mediaResourceType } } 
      }
    )) as GraphQLResponse<InitiateUploadData>;
    if (initialErrors && initialErrors.length) throw new Error(formatGraphQLErrors(initialErrors));

    const { upload_url, key } = initialData?.initiateUpload || {};
    if (!upload_url || !key) throw new Error('Failed to initiate upload');

    const put = await fetch(upload_url, { method: 'PUT', body: file });
    if (!put.ok) throw new Error(`Upload failed: ${put.status} ${put.statusText}`);

    const { data: finishData, errors: finishErrors } = (await fetchGraphQl(
      FINISH_UPLOAD_MUTATION,
      { 
        variables: { input: { key, media_resource_type: mediaResourceType } } 
      }
    )) as GraphQLResponse<FinishUploadData>;
    if (finishErrors && finishErrors.length) throw new Error(formatGraphQLErrors(finishErrors));

    const { success, key: finalizedKey, message } = finishData?.finishUpload || {};
    if (!success || !finalizedKey) throw new Error(message || 'Failed to finish upload');

    return { key: finalizedKey };
    
  } catch (err: any) {
    events.emit('quote-management/file-upload-error', {
      error: err?.message || 'File upload failed',
      fileName: file?.name,
    });
    throw err instanceof Error ? err : new Error('File upload failed');
  }
};
